import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '../components/page-action-bar/page-action-bar';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/select/select.js';
import '@shoelace-style/shoelace/dist/components/option/option.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import {
  IPurchaseOrder,
  IPurchaseOrderLineRequest,
  IStockItem,
  ISupplier,
  PurchaseOrderStatus,
} from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

const STATUS_VARIANT: Record<
  PurchaseOrderStatus,
  'neutral' | 'primary' | 'warning' | 'success' | 'danger'
> = {
  draft: 'neutral',
  placed: 'primary',
  partially_received: 'warning',
  received: 'success',
  cancelled: 'danger',
};

// Combined create + edit page. /purchase-orders/new for create,
// /purchase-orders/:id for edit. Line edits are allowed only while
// Draft (server-enforced). For Placed / PartiallyReceived / Received /
// Cancelled, the page renders read-only with the actions surfaced
// at the foot (Place when Draft, Cancel when Draft / Placed).
@customElement('purchase-order-edit-page')
export class PurchaseOrderEditPage extends Base {
  @state() private _existing: IPurchaseOrder | null = null;
  @state() private _loading = true;
  @state() private _busy = false;
  @state() private _error: string | null = null;
  @state() private _saved = false;

  // Lookups for pickers.
  @state() private _suppliers: ISupplier[] = [];
  @state() private _stock: IStockItem[] = [];

  // Form fields.
  @state() private _supplierId = '';
  @state() private _expectedDeliveryUtc = '';
  @state() private _referenceNumber = '';
  @state() private _note = '';
  @state() private _lines: IPurchaseOrderLineRequest[] = [];

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        max-width: 900px;
        margin: 0 auto;
        padding: 16px;
        box-sizing: border-box;
      }
      @media (max-width: 720px) {
        :host {
          padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px));
        }
      }
      header.bar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
      }
      header.bar h2 {
        margin: 0;
        flex: 1;
      }
      form {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      .row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }
      .line-row {
        display: grid;
        grid-template-columns: 1fr 110px 110px 32px;
        gap: 8px;
        align-items: end;
      }
      .line-row .small {
        font-size: 0.78rem;
        opacity: 0.7;
      }
      .line-row.locked {
        grid-template-columns: 1fr 110px 110px 110px;
      }
      .right {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
      .small-add {
        margin-top: 4px;
      }
      footer {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        margin-top: 16px;
        flex-wrap: wrap;
      }
      .summary {
        font-variant-numeric: tabular-nums;
        opacity: 0.85;
      }
    `,
  ];

  firstUpdated() {
    void this._load();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop) void this._load();
  }

  private async _load() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    this._loading = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    const [supR, stockR] = await Promise.all([
      api.listSuppliers(this.activeShop.shopName),
      api.listStock(this.activeShop.shopName),
    ]);
    if (supR.data) this._suppliers = supR.data.filter((s) => s.isActive);
    if (stockR.data) this._stock = stockR.data.filter((s) => s.isActive);

    const editId = readEditIdFromPath();
    if (editId && editId !== 'new') {
      const r = await api.getPurchaseOrder(this.activeShop.shopName, editId);
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Purchase order not found.';
        this._loading = false;
        return;
      }
      const po = r.data;
      this._existing = po;
      this._supplierId = po.supplierId;
      this._expectedDeliveryUtc = po.expectedDeliveryUtc?.slice(0, 16) ?? '';
      this._referenceNumber = po.referenceNumber ?? '';
      this._note = po.note ?? '';
      this._lines = po.lines.map((l) => ({
        stockItemId: l.stockItemId,
        quantityInPurchaseUnit: l.quantityInPurchaseUnit,
        unitCostInPurchaseUnit: l.unitCostInPurchaseUnit,
      }));
    }
    this._loading = false;
  }

  private get _isEdit(): boolean {
    return this._existing !== null;
  }

  private get _isDraftEditable(): boolean {
    // Server allows line edits only while Draft. The form is read-only
    // for any non-Draft existing PO.
    return !this._existing || this._existing.status === 'draft';
  }

  private get _subtotal(): number {
    return this._lines.reduce(
      (sum, l) =>
        sum + (l.quantityInPurchaseUnit || 0) * (l.unitCostInPurchaseUnit || 0),
      0
    );
  }

  private _addLine() {
    this._lines = [
      ...this._lines,
      { stockItemId: '', quantityInPurchaseUnit: 0, unitCostInPurchaseUnit: 0 },
    ];
    this._saved = false;
  }
  private _removeLine(i: number) {
    this._lines = this._lines.filter((_, idx) => idx !== i);
    this._saved = false;
  }
  private _updateLine(i: number, patch: Partial<IPurchaseOrderLineRequest>) {
    this._lines = this._lines.map((l, idx) =>
      idx === i ? { ...l, ...patch } : l
    );
    this._saved = false;
  }

  private async _save() {
    if (!this.activeShop || this._busy) return;
    if (!this._supplierId) {
      this._error = 'Supplier is required.';
      return;
    }
    if (!this._existing && this._lines.length === 0) {
      this._error = 'Add at least one line.';
      return;
    }
    for (const l of this._lines) {
      if (
        !l.stockItemId ||
        l.quantityInPurchaseUnit <= 0 ||
        l.unitCostInPurchaseUnit < 0
      ) {
        this._error =
          'Each line needs a stock item, qty > 0, and non-negative unit cost.';
        return;
      }
    }
    this._busy = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    if (this._existing) {
      const r = await api.updatePurchaseOrder(
        this.activeShop.shopName,
        this._existing.id,
        {
          expectedDeliveryUtc: this._expectedDeliveryUtc
            ? new Date(this._expectedDeliveryUtc).toISOString()
            : null,
          referenceNumber: this._referenceNumber.trim() || null,
          note: this._note.trim() || null,
          lines: this._lines,
        }
      );
      this._busy = false;
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Save failed.';
        return;
      }
      this._existing = r.data;
      this._saved = true;
    } else {
      const r = await api.createPurchaseOrder(this.activeShop.shopName, {
        supplierId: this._supplierId,
        expectedDeliveryUtc: this._expectedDeliveryUtc
          ? new Date(this._expectedDeliveryUtc).toISOString()
          : undefined,
        referenceNumber: this._referenceNumber.trim() || undefined,
        note: this._note.trim() || undefined,
        lines: this._lines,
      });
      this._busy = false;
      if (!r.success || !r.data) {
        this._error = r.error ?? 'Create failed.';
        return;
      }
      router.navigate(
        resolveRouterPath(`purchase-orders/${encodeURIComponent(r.data.id)}`)
      );
    }
  }

  private async _place() {
    if (!this.activeShop || !this._existing || this._busy) return;
    if (
      !confirm(
        'Place this purchase order? Lines will be locked from this point.'
      )
    )
      return;
    this._busy = true;
    const r = await ApiFactory.authenticated().placePurchaseOrder(
      this.activeShop.shopName,
      this._existing.id
    );
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Place failed.';
      return;
    }
    this._existing = r.data;
  }

  private async _cancel() {
    if (!this.activeShop || !this._existing || this._busy) return;
    const reason = prompt('Cancellation reason (optional):') ?? undefined;
    this._busy = true;
    const r = await ApiFactory.authenticated().cancelPurchaseOrder(
      this.activeShop.shopName,
      this._existing.id,
      { reason: reason || undefined }
    );
    this._busy = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Cancel failed.';
      return;
    }
    this._existing = r.data;
  }

  private _back() {
    router.navigate(resolveRouterPath('purchase-orders'));
  }

  private _stockName(id: string): string {
    return this._stock.find((s) => s.id === id)?.name ?? id;
  }

  private _renderLines() {
    if (!this._isDraftEditable) {
      return html`
        <h3>Lines</h3>
        ${this._existing?.lines.map(
          (l) => html`
            <div class="line-row locked">
              <div>
                <strong>${this._stockName(l.stockItemId)}</strong>
                <div class="small">
                  ${l.supplierSkuSnapshot ? `SKU ${l.supplierSkuSnapshot}` : ''}
                </div>
              </div>
              <div class="right">${l.quantityInPurchaseUnit}</div>
              <div class="right">${l.unitCostInPurchaseUnit.toFixed(2)}</div>
              <div class="right">
                <small class="small">received</small>
                <div>
                  ${l.receivedInPurchaseUnit} / ${l.quantityInPurchaseUnit}
                </div>
              </div>
            </div>
          `
        )}
      `;
    }
    return html`
      <h3>Lines</h3>
      ${
        this._lines.length === 0
          ? html`<p class="small-add">No lines yet — add at least one.</p>`
          : nothing
      }
      ${this._lines.map(
        (line, i) => html`
          <div class="line-row">
            <sl-select
              .value=${line.stockItemId}
              placeholder="Pick a stock item"
              @sl-change=${(e: Event) =>
                this._updateLine(i, {
                  stockItemId: (e.target as HTMLSelectElement).value,
                })}
            >
              ${this._stock.map(
                (s) =>
                  html`<sl-option value=${s.id}
                    >${s.name} (${s.baseUnit})</sl-option
                  >`
              )}
            </sl-select>
            <sl-input
              type="number"
              min="0"
              step="any"
              label="Qty"
              .value=${String(line.quantityInPurchaseUnit)}
              @sl-input=${(e: Event) =>
                this._updateLine(i, {
                  quantityInPurchaseUnit:
                    parseFloat((e.target as HTMLInputElement).value) || 0,
                })}
            ></sl-input>
            <sl-input
              type="number"
              min="0"
              step="any"
              label="Unit cost"
              .value=${String(line.unitCostInPurchaseUnit)}
              @sl-input=${(e: Event) =>
                this._updateLine(i, {
                  unitCostInPurchaseUnit:
                    parseFloat((e.target as HTMLInputElement).value) || 0,
                })}
            ></sl-input>
            <sl-icon-button
              name="trash"
              label="Remove"
              @click=${() => this._removeLine(i)}
            ></sl-icon-button>
          </div>
        `
      )}
      <sl-button size="small" class="small-add" @click=${this._addLine}
        >Add line</sl-button
      >
    `;
  }

  render() {
    if (this._loading) return html`<sl-card><p>Loading…</p></sl-card>`;
    const subtotal = this._subtotal;
    const status = this._existing?.status ?? 'draft';

    return html`
      <header class="bar">
        <sl-icon-button
          name="arrow-left"
          label="Back"
          @click=${this._back}
        ></sl-icon-button>
        <h2>${this._isEdit ? 'Purchase order' : 'New purchase order'}</h2>
        ${
          this._existing
            ? html`<sl-badge variant=${STATUS_VARIANT[status]} pill
                >${status.replace('_', ' ')}</sl-badge
              >`
            : nothing
        }
      </header>

      ${this._error ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>` : nothing}
      ${
        this._saved
          ? html`<sl-alert variant="success" open>Saved.</sl-alert>`
          : nothing
      }

      <sl-card>
        <form
          @submit=${(e: Event) => {
            e.preventDefault();
            void this._save();
          }}
        >
          <div class="row">
            <sl-select
              label="Supplier"
              .value=${this._supplierId}
              ?disabled=${this._isEdit}
              @sl-change=${(e: Event) => {
                this._supplierId = (e.target as HTMLSelectElement).value;
                this._saved = false;
              }}
              required
            >
              ${this._suppliers.map(
                (s) => html`<sl-option value=${s.id}>${s.name}</sl-option>`
              )}
            </sl-select>
            <sl-input
              type="datetime-local"
              label="Expected delivery"
              .value=${this._expectedDeliveryUtc}
              ?disabled=${!this._isDraftEditable}
              @sl-input=${(e: Event) => {
                this._expectedDeliveryUtc = (
                  e.target as HTMLInputElement
                ).value;
                this._saved = false;
              }}
            ></sl-input>
          </div>

          <sl-input
            label="Reference number"
            help-text="The supplier's PO ref, optional. Goes on the printed PO."
            .value=${this._referenceNumber}
            ?disabled=${!this._isDraftEditable}
            @sl-input=${(e: Event) => {
              this._referenceNumber = (e.target as HTMLInputElement).value;
              this._saved = false;
            }}
          ></sl-input>

          <sl-textarea
            label="Note"
            rows="2"
            .value=${this._note}
            ?disabled=${!this._isDraftEditable}
            @sl-input=${(e: Event) => {
              this._note = (e.target as HTMLInputElement).value;
              this._saved = false;
            }}
          ></sl-textarea>

          <sl-divider></sl-divider>
          ${this._renderLines()}

          <div class="summary right">
            Subtotal: <strong>${subtotal.toFixed(2)}</strong>
          </div>
        </form>
      </sl-card>

      <page-action-bar>
        ${
          this._existing && (status === 'draft' || status === 'placed')
            ? html`<sl-button
                slot="left"
                variant="warning"
                @click=${this._cancel}
                ?disabled=${this._busy}
                >Cancel</sl-button
              >`
            : nothing
        }
        ${
          this._existing && status === 'draft'
            ? html`<sl-button
                slot="right"
                variant="success"
                @click=${this._place}
                ?disabled=${this._busy}
                >Place</sl-button
              >`
            : nothing
        }
        ${
          this._isDraftEditable
            ? html`
                <sl-button
                  slot="right"
                  variant="primary"
                  ?loading=${this._busy}
                  @click=${this._save}
                >
                  ${this._isEdit ? 'Save draft' : 'Create draft'}
                </sl-button>
              `
            : nothing
        }
      </page-action-bar>
    `;
  }
}

function readEditIdFromPath(): string | null {
  const m = window.location.pathname.match(/\/purchase-orders\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}
